/**
 * @file useOrderList
 * @description 拉取用户订单记录列表（GET /payment/order/list）
 * - 仅登录后启用
 * - 通过 hasMore 分页；按 create_time DESC、order_no DESC 由服务端排序
 */

import {useInfiniteQuery} from '@tanstack/react-query';
import {getPaymentOrderList} from '@/services/commerce/order';
import type {PaymentOrderListItem} from '@/services/commerce/order/interface';
import {useAuthStore} from '@/store/useAuthStore';

export const ORDER_LIST_PAGE_SIZE = 20;

export const orderListKeys = {
    all: ['commerce.orderList'] as const,
    list: (pageSize: number) => [...orderListKeys.all, pageSize] as const,
};

export interface UseOrderListResult {
    items: PaymentOrderListItem[];
    total: number;
    isLoading: boolean;
    isError: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    /** 分页加载失败（首屏之后触底拉取下一页时的错误） */
    isFetchNextPageError: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    refetch: () => void;
}

export function useOrderList(
    enabled: boolean = true,
    pageSize: number = ORDER_LIST_PAGE_SIZE,
): UseOrderListResult {
    const isLogin = useAuthStore((state) => state.isLogin);

    const query = useInfiniteQuery({
        queryKey: orderListKeys.list(pageSize),
        queryFn: ({pageParam}) => getPaymentOrderList({pageNo: pageParam, pageSize}),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.pageNo + 1 : undefined),
        enabled: isLogin && enabled,
        staleTime: 0,
        gcTime: 30 * 60 * 1000,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
    });

    return {
        items: query.data?.pages.flatMap((p) => p.items) ?? [],
        total: query.data?.pages[0]?.total ?? 0,
        isLoading: query.isLoading,
        isError: query.isError,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        isFetchNextPageError: query.isFetchNextPageError,
        hasNextPage: query.hasNextPage,
        fetchNextPage: () => {
            void query.fetchNextPage();
        },
        refetch: () => {
            void query.refetch();
        },
    };
}
